// ─────────────────────────────────────────────────────────────────────────────
// DetailsStep (1/3) — the endpoint the node will be registered at.
//
// Validation mirrors the backend's isPublicIp() closely enough to catch the
// mistake it rejects most often, and no more. The server remains the authority:
// anything that gets past this is sent, and a 400 comes back with the field-level
// reason (surfaced as `error`).
//
// The address must be a ROUTABLE PUBLIC IP. The placeholder used to read
// "10.0.5.1", which is RFC1918 private — the endpoint refuses it outright, so the
// example was steering admins straight into a rejection. Enterprise nodes have to
// be reachable on their public address.
// ─────────────────────────────────────────────────────────────────────────────
import styles from "./AddNodeWizard.module.css";
import { ChevronRightIcon } from "./icons";

/**
 * Local pre-flight for the obvious rejects, matching the server's rules:
 * loopback/unspecified (0.x, 127.x), RFC1918 private, link-local, IPv6 loopback,
 * ULA (fc00::/7) and link-local (fe80::/10). Returns a message or null.
 *
 * Deliberately NOT a full IP validator — the server owns that. This only exists
 * so the common case fails instantly and in place, instead of after a round trip.
 */
function localIpProblem(raw) {
  const ip = raw.trim().toLowerCase();
  if (ip === "") return null;
  if (ip === "localhost") return "Use a routable public IP — localhost is not reachable from the internet.";

  const v4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(ip);
  if (v4) {
    const parts = v4.slice(1).map(Number);
    if (parts.some((o) => o > 255)) return "That isn't a valid IPv4 address.";
    const [a, b] = parts;
    if (a === 0 || a === 127) return "That is a loopback/unspecified address. Use the node's public IP.";
    if (a === 10) return "10.x.x.x is a private range. Use the node's public IP.";
    if (a === 192 && b === 168) return "192.168.x.x is a private range. Use the node's public IP.";
    if (a === 172 && b >= 16 && b <= 31) return "172.16–31.x.x is a private range. Use the node's public IP.";
    return null;
  }

  if (ip.includes(":")) {
    if (!/^[0-9a-f:]+$/.test(ip)) return "That isn't a valid IPv6 address.";
    if (ip === "::1") return "That is the IPv6 loopback. Use the node's public IP.";
    if (ip.startsWith("fc") || ip.startsWith("fd")) return "That is a private IPv6 range. Use the node's public IP.";
    if (ip.startsWith("fe80")) return "That is IPv6 link-local. Use the node's public IP.";
    return null;
  }

  return "That isn't a valid IP address.";
}

export default function DetailsStep({ form, error, onChange, onContinue }) {
  const ipProblem = localIpProblem(form.ip);
  const filled = form.ip.trim() !== "" && form.name.trim() !== "";
  const ready = filled && !ipProblem;

  const submit = (e) => {
    e.preventDefault();
    if (ready) onContinue();
  };

  return (
    <form className={styles.step} onSubmit={submit}>
      <div className={styles.stepHead}>
        <h2 className={styles.title} id="add-node-title">Add New Node</h2>
        <p className={styles.subtitle}>Configure the new node endpoint and agent package.</p>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="add-node-ip">Public IP Address</label>
        <input
          id="add-node-ip"
          className={styles.input}
          placeholder="e.g. 203.0.113.10"
          autoComplete="off"
          spellCheck={false}
          value={form.ip}
          onChange={(e) => onChange({ ip: e.target.value })}
          aria-invalid={Boolean(ipProblem)}
        />
        {/* Location is derived from this address server-side — it isn't asked for
            anywhere in this form, and cannot be overridden. */}
        <p className={styles.subtitle}>
          The node&apos;s routable public address. Its region and coordinates are
          detected from it.
        </p>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="add-node-name">Node Name</label>
        <input
          id="add-node-name"
          className={styles.input}
          placeholder="e.g. N-US-W2"
          autoComplete="off"
          value={form.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </div>

      {/* Local check first — it's about the field the cursor is in. The server's
          reason (from a previous attempt) only shows once that passes. */}
      {ipProblem && <p className={styles.subtitle} role="alert">{ipProblem}</p>}
      {!ipProblem && error && <p className={styles.subtitle} role="alert">{error}</p>}

      <button type="submit" className={styles.btnPrimary} disabled={!ready}>
        Continue <ChevronRightIcon />
      </button>
    </form>
  );
}
